import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { KeyValues, Notice, StatusPill } from "../../../design/components";
import { METHODS } from "../../../design/methods";
import { HEALTH_PILL, METHOD_BY_CONNECTION } from "../../../domain/health";
import { familyLabel, normaliseFamilies } from "../../../domain/families";
import { useDocuments, useEntityHealth, usePauseEntity, useResumeEntity } from "../../../api/queries";
import { dateTime, omr, relative } from "../../../lib/format";
import type { Entity } from "../../../api/types";
import { statePill, StageCell } from "../../documents/DocumentRow";
import styles from "../CompanyDetail.module.css";

/** HUB-I08: stop/resume this company's claims — a read/write control, never a change to
 * the deployed replica count (that stays a deployment action, HUB-I09/I04). */
function PauseControl({ entity }: { entity: Entity }) {
  const [reason, setReason] = useState("");
  const pause = usePauseEntity(entity.id);
  const resume = useResumeEntity(entity.id);

  if (entity.pausedReason) {
    return (
      <div className={styles.pauseControl}>
        <Notice kind="warn">Paused: {entity.pausedReason}</Notice>
        {resume.isError ? <Notice kind="fail">Could not resume this company.</Notice> : null}
        <button
          type="button"
          className="btn"
          disabled={resume.isPending}
          onClick={() => resume.mutate()}
        >
          Resume
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pauseControl}>
      {pause.isError ? <Notice kind="fail">Could not pause this company.</Notice> : null}
      <div className="row">
        <input
          className="input grow"
          placeholder="Reason for pausing (required)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-label="Reason for pausing"
        />
        <button
          type="button"
          className="btn"
          disabled={pause.isPending || !reason.trim()}
          onClick={() => pause.mutate(reason.trim(), { onSuccess: () => setReason("") })}
        >
          Pause
        </button>
      </div>
    </div>
  );
}

export function OverviewTab({ entity }: { entity: Entity }) {
  const { t } = useTranslation();
  const health = useEntityHealth(entity.id);
  const documents = useDocuments({ entityId: entity.id, limit: 10 });
  const recent = documents.data?.pages[0]?.items ?? [];
  const mismatch = entity.participantId !== `0248:${entity.crNumber}`;
  const method = METHODS[METHOD_BY_CONNECTION[entity.connectionMethod]];
  const connector = health.data?.connector;

  return (
    <div className={styles.overview}>
      {mismatch ? (
        <Notice kind="fail">
          {t(
            "companies.overview.participantMismatch",
            "Participant ID does not match the commercial registration number. Outbound documents for this company will be stopped before they are sent.",
          )}
        </Notice>
      ) : null}

      <div className={styles.columns}>
        <section className="card">
          <div className="card-head">
            <h2 className="grow">{t("companies.overview.identityHeading", "Identity and setup")}</h2>
          </div>
          <div className="card-body">
            <KeyValues
              pairs={[
                [t("companies.overview.fields.legalName", "Legal name"), entity.legalName],
                [t("companies.overview.fields.companyCode", "Company code"), <span className="mono">{entity.code}</span>],
                [t("companies.overview.fields.crNumber", "CR number"), <span className="mono">{entity.crNumber}</span>],
                [
                  t("companies.overview.fields.vatin", "VAT identification number"),
                  <span className="mono">{entity.vatin ?? "—"}</span>,
                ],
                [
                  t("companies.overview.fields.participantId", "Participant ID"),
                  <span className="mono">{entity.participantId}</span>,
                ],
                [t("companies.overview.fields.erp", "ERP"), entity.erpType],
                [t("companies.overview.fields.erpVersion", "ERP version"), entity.erpVersion ?? "—"],
                [t("companies.overview.fields.connectionMethod", "Connection method"), method.full],
                [t("companies.overview.fields.wave", "Wave"), entity.wave ?? "—"],
                [
                  t("companies.overview.fields.documentFamilies", "Document families"),
                  // A company saved before families existed carries names that fold into one family.
                  entity.enabledDocTypes.length
                    ? normaliseFamilies(entity.enabledDocTypes).map(familyLabel).join(", ")
                    : "—",
                ],
                [
                  t("companies.overview.fields.activeMappingVersion", "Active mapping profile version"),
                  entity.activeProfileVersionId ?? "—",
                ],
                [
                  t("companies.overview.fields.silenceThreshold", "Silence threshold"),
                  t("companies.overview.minutes", "{{count}} minutes", { count: entity.silenceThresholdMinutes }),
                ],
              ]}
            />
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h2 className="grow">{t("companies.overview.healthHeading", "Health")}</h2>
            {health.data ? <StatusPill {...HEALTH_PILL[health.data.health]} /> : null}
          </div>
          <div className="card-body">
            {health.isError ? (
              <Notice kind="warn">
                {t("companies.overview.healthLoadError", "The health of this company could not be read.")}
              </Notice>
            ) : health.data ? (
              <KeyValues
                pairs={[
                  [
                    t("companies.overview.fields.connector", "Connector"),
                    connector?.reachable
                      ? t("companies.overview.reachable", "Reachable")
                      : t("companies.overview.unreachable", "Unreachable"),
                  ],
                  [
                    t("companies.overview.fields.checked", "Checked"),
                    connector?.checkedAt ? dateTime(connector.checkedAt) : "—",
                  ],
                  [t("companies.overview.fields.detail", "Detail"), connector?.detail ?? "—"],
                  [
                    t("companies.overview.fields.lastDocument", "Last document"),
                    health.data.lastDocumentSeenAt ? relative(health.data.lastDocumentSeenAt) : "—",
                  ],
                  ["In flight", health.data.inFlight],
                  ["Terminal exceptions", health.data.terminalExceptions],
                  ["Silence threshold", `${entity.silenceThresholdMinutes} minutes`],
                  [
                    "Recommended workers",
                    `${entity.recommendedWorkers} (of ${entity.currentPipelineReplicas} deployed group-wide)`,
                  ],
                ]}
              />
            ) : (
              <p className={styles.muted}>{t("companies.overview.loading", "Loading…")}</p>
            )}
            <PauseControl entity={entity} />
          </div>
        </section>
      </div>

      <section className="card card-tight">
        <div className="card-head">
          <h2 className="grow">{t("companies.overview.recentDocsHeading", "Recent documents")}</h2>
          <Link className="link" to={`/documents?entityId=${entity.id}`}>
            {t("companies.overview.seeAll", "See all")}
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="card-body">
            <Notice kind="info">{t("companies.overview.noDocuments", "No documents for this company yet.")}</Notice>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("companies.overview.columns.stage", "Stage")}</th>
                  <th>{t("companies.overview.columns.invoice", "Invoice")}</th>
                  <th className="right">{t("companies.overview.columns.total", "Total")}</th>
                  <th>{t("companies.overview.columns.status", "Status")}</th>
                  <th className="nowrap">{t("companies.overview.columns.updated", "Updated")}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <StageCell row={row} />
                    </td>
                    <td className="mono nowrap">
                      <Link className="link" to={`/documents/${row.id}`}>
                        {row.erpDocId ?? row.id}
                      </Link>
                    </td>
                    <td className="right mono nowrap">
                      {row.totalAmount === null ? "—" : omr(Number(row.totalAmount))}
                    </td>
                    <td>
                      <StatusPill {...statePill(row.state)} />
                    </td>
                    <td className="nowrap">{relative(row.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
